'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Dialog, DialogActionClose, DialogActionPrimary, DialogActions, DialogContent } from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { Sheet, SheetActions, SheetBody, SheetContent, SheetFooter, SheetHeader, SheetTrigger } from '@sola/ark-core/molecules/sheet/sheet.tsx';
import { StepperTabbed } from '@sola/ark-core/molecules/stepper-tabbed/stepper-tabbed.tsx';
import { useRouter } from '@sola/ark-core/utils/hooks/useRouter.ts';
import type { VolunteerLocationItemDto } from '@sola/skippy-search-sdk/lib/schema.ts';
import { trackVolunteerComplete, trackVolunteerStep } from '@sola/tracking/lib/analytics/events-volunteer.ts';
import { slashes } from '@sola/utils/url.ts';
import { useNavigationGuard } from '../../hooks/use-navigation-guard.ts';
import { isEventFullError } from './errors/volunteer-conflict-errors.ts';
import { mapSubmitErrorToFormErrors, shouldBounceToShifts } from './errors/volunteer-errors.logic.ts';
import { PanelConfirmation } from './panels/panel-confirmation.tsx';
import { PanelPeople } from './panels/panel-people.tsx';
import { PanelShifts } from './panels/panel-shifts.tsx';
import { PanelWaivers } from './panels/panel-waivers.tsx';
import { useVolunteerRegistrationSheet } from './volunteer-registration-sheet.logic.ts';
export const VolunteerRegistrationSheet: React.FC<{
  eventId: string;
  trigger: React.ReactElement;
  defaultOpen?: boolean;
  location?: VolunteerLocationItemDto;
  onEventFull?: () => void;
}> = ({
  eventId,
  trigger,
  defaultOpen = false,
  location,
  onEventFull
}) => {
  const {
    t
  } = useTranslation(['volunteers']);
  const {
    router,
    usePathname
  } = useRouter();
  const pathname = usePathname();
  const logic = useVolunteerRegistrationSheet(eventId);
  const [sheetOpen, setSheetOpen] = useState(defaultOpen);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [eventFullOpen, setEventFullOpen] = useState(false);
  const [unassignedWarningOpen, setUnassignedWarningOpen] = useState(false);
  const [completedStep, setCompletedStep] = useState(-1);
  const [submitting, setSubmitting] = useState(false);
  const [slowSubmit, setSlowSubmit] = useState(false);
  const slowSubmitTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Begins/ends the "Submitting… → Almost there…" escalation.
  const startSlowSubmitTimer = () => {
    setSlowSubmit(false);
    slowSubmitTimerRef.current = setTimeout(() => setSlowSubmit(true), 4000);
  };
  const clearSlowSubmitTimer = () => {
    clearTimeout(slowSubmitTimerRef.current);
    setSlowSubmit(false);
  };

  // Cancel any pending slow-submit timer on unmount
  useEffect(() => () => clearTimeout(slowSubmitTimerRef.current), []);

  // Defer the sheet close + reset until the user returns from confirmation.
  const pendingCompletionRef = useRef(false);
  const fireStepTracking = (stepIndex: number) => {
    if (!location) {
      return;
    }
    trackVolunteerStep(logic.buildTrackingParams(stepIndex, location));
  };

  // Latch open on defaultOpen's false→true edge (survives the URL param being
  // stripped, which flips defaultOpen back to false). Also fire step-0 tracking
  // for auto-opened sheets (deep-link path) on that same edge.
  const prevDefaultOpenRef = useRef(defaultOpen);
  if (defaultOpen && !prevDefaultOpenRef.current) {
    setSheetOpen(true);
    fireStepTracking(0);
  }
  prevDefaultOpenRef.current = defaultOpen;

  // On return from confirmation, close and reset for a fresh registration.
  const prevPathnameRef = useRef(pathname);
  if (prevPathnameRef.current !== pathname) {
    prevPathnameRef.current = pathname;
    if (pendingCompletionRef.current && !pathname.includes('confirmation')) {
      pendingCompletionRef.current = false;
      setSheetOpen(false);
      setSubmitting(false);
      clearSlowSubmitTimer();
      setCompletedStep(-1);
      logic.resetFlow();
      logic.refreshSlots();
    }
  }

  // Guard back/forward while open, except during submit (see release() below).
  const handleNavigationBlock = () => {
    setConfirmCloseOpen(true);
  };
  const {
    release: releaseNavGuard
  } = useNavigationGuard(sheetOpen && !submitting, handleNavigationBlock);

  // Notify the parent once (feed detection or submit path) to disable the trigger.
  const eventFullNotifiedRef = useRef(false);
  const notifyEventFull = () => {
    if (!eventFullNotifiedRef.current) {
      eventFullNotifiedRef.current = true;
      onEventFull?.();
    }
  };

  // Feed-based event-full signal (the submit path uses EventFullError instead).
  // Alert then close so the sheet doesn't vanish silently; not while submitting.
  const eventIsFull = !logic.slotsPending && !logic.slotsError && logic.totalAvailableSlots === 0;
  if (sheetOpen && !submitting && eventIsFull && !eventFullOpen) {
    setEventFullOpen(true);
  }
  useEffect(() => {
    if (eventIsFull) {
      notifyEventFull();
    }
  }, [eventIsFull]);
  const handleEventFullAcknowledge = () => {
    setEventFullOpen(false);
    releaseNavGuard();
    setSheetOpen(false);
    setCompletedStep(-1);
    logic.resetFlow();
  };

  // Invalidate future steps on earlier panel data changes
  const prevContactIdsRef = useRef(logic.selectedContactIds);
  const prevAssignmentsRef = useRef(logic.assignments);
  if (prevContactIdsRef.current !== logic.selectedContactIds || prevAssignmentsRef.current !== logic.assignments) {
    prevContactIdsRef.current = logic.selectedContactIds;
    prevAssignmentsRef.current = logic.assignments;
    if (completedStep > logic.step) {
      setCompletedStep(logic.step);
    }
  }
  const getNextButtonLabel = () => {
    if (!logic.isLastStep) {
      return t('volunteers:registration.continue');
    }
    if (!submitting) {
      return t('volunteers:registration.completeRegistration');
    }
    return slowSubmit ? t('volunteers:registration.almostThere') : t('volunteers:registration.submitting');
  };
  const nextButtonLabel = getNextButtonLabel();
  const fireCompleteTracking = () => {
    if (!location) {
      return;
    }
    trackVolunteerComplete(logic.buildTrackingParams(logic.step, location));
  };
  const handleSheetOpenChange = (open: boolean) => {
    if (open) {
      logic.setStep(0);
      setCompletedStep(-1);
      setSheetOpen(true);
      fireStepTracking(0);
      return;
    }
    // Lock the sheet closed while the registration payload is in flight.
    if (submitting) {
      return;
    }
    setConfirmCloseOpen(true);
  };
  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    releaseNavGuard();
    setSheetOpen(false);
    setCompletedStep(-1);
    logic.resetFlow();
  };
  const handleConfirmCloseCancel = () => {
    setConfirmCloseOpen(false);
  };
  const handleSubmitError = (error: unknown) => {
    setSubmitting(false);
    clearSlowSubmitTimer();
    // Event full: alert + close here instead of bouncing to shifts.
    if (isEventFullError(error)) {
      notifyEventFull();
      setEventFullOpen(true);
      return;
    }
    // Bounce back via setStep (not refreshSlots) so the backend-rejected slot
    // set survives the retry — refreshSlots clears it and would reopen the loop.
    if (shouldBounceToShifts(error)) {
      logic.setStep(1);
      setCompletedStep(0);
    }
    logic.setFormErrors(mapSubmitErrorToFormErrors(error));
  };
  const handleNext = async () => {
    // Warn if some contacts have no shifts (panel 2: shifts)
    if (logic.step === 1 && logic.contactsWithoutShifts.length > 0) {
      setUnassignedWarningOpen(true);
      return;
    }
    setCompletedStep(prev => Math.max(prev, logic.step));
    if (logic.isLastStep) {
      // Disables the nav guard before navigating, avoiding an errant
      // release() history.back() against a stale pathname.
      setSubmitting(true);
      logic.clearFormErrors();
      startSlowSubmitTimer();
      try {
        await logic.handleNext(() => {
          // Keep sheet open + `submitting` true through the redirect; the
          // pathname-transition block resets both on return.
          pendingCompletionRef.current = true;
          fireCompleteTracking();
          // `?submitted=1` marks a post-submit arrival, to show the alert.
          router.push(`${slashes(pathname, 'both')}confirmation/?submitted=1`);
        });
      } catch (error_0) {
        handleSubmitError(error_0);
      }
    } else {
      logic.handleNext();
      fireStepTracking(logic.step + 1);
    }
  };
  const handleConfirmUnassigned = async () => {
    setUnassignedWarningOpen(false);
    setCompletedStep(prev_0 => Math.max(prev_0, logic.step));
    try {
      await logic.handleNext();
      fireStepTracking(logic.step + 1);
    } catch (error_1) {
      handleSubmitError(error_1);
    }
  };
  const stepItems = [{
    title: t('volunteers:registration.people'),
    body: <PanelPeople logic={logic} location={location} />
  }, {
    title: t('volunteers:registration.selectShifts'),
    body: <PanelShifts logic={logic} />
  }, {
    title: t('volunteers:registration.signWaivers'),
    body: <PanelWaivers logic={logic} />
  }, {
    title: t('volunteers:registration.confirmInfo'),
    body: <PanelConfirmation logic={logic} />
  }];
  return <>
      <Sheet open={sheetOpen} onOpenChange={handleSheetOpenChange}>
        <SheetTrigger>{trigger}</SheetTrigger>
        <SheetContent title={t('volunteers:registration.title')}>
          <SheetHeader>{t('volunteers:registration.title')}</SheetHeader>
          <SheetBody>
            <StepperTabbed items={stepItems}
          // Lock all tabs while submitting.
          maxStep={submitting ? -1 : completedStep + 1} activeStep={logic.step} onStepChange={logic.setStep} />
          </SheetBody>
          <SheetFooter>
            <SheetActions>
              {logic.step === 0 ? <ButtonStyle variant="secondary" size="lg">
                  <button type="button" onClick={() => setConfirmCloseOpen(true)} className="flex-1">
                    {t('volunteers:registration.cancelSignup')}
                  </button>
                </ButtonStyle> : <ButtonStyle variant="secondary" size="lg">
                  <button type="button" onClick={logic.handleBack} disabled={submitting} className="flex-1">
                    {t('volunteers:registration.back')}
                  </button>
                </ButtonStyle>}
              <ButtonStyle variant="primary" size="lg" className="flex-1" isBusy={submitting}>
                <button type="button" onClick={handleNext} disabled={!logic.canAdvance || submitting} className="w-full">
                  {nextButtonLabel}
                </button>
              </ButtonStyle>
            </SheetActions>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Close confirmation dialog */}
      <Dialog open={confirmCloseOpen} onOpenChange={open_0 => {
      if (!open_0) {
        handleConfirmCloseCancel();
      }
    }}>
        <DialogContent title={t('volunteers:registration.closeConfirmTitle')} supportingText={t('volunteers:registration.closeConfirmMessage')} purpose="negative" icon="alert-circle">
          <DialogActions>
            <DialogActionClose>
              {t('volunteers:registration.closeConfirmNo')}
            </DialogActionClose>
            <DialogActionPrimary onClick={handleConfirmClose} purpose="negative">
              {t('volunteers:registration.closeConfirmYes')}
            </DialogActionPrimary>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* Event full dialog: event ran out of slots mid-signup */}
      <Dialog open={eventFullOpen} onOpenChange={open_1 => {
      if (!open_1) {
        handleEventFullAcknowledge();
      }
    }}>
        <DialogContent title={t('volunteers:registration.eventFullTitle')} supportingText={t('volunteers:registration.eventFullError')} purpose="negative" icon="alert-circle">
          <DialogActions>
            <DialogActionPrimary onClick={handleEventFullAcknowledge} purpose="negative">
              {t('volunteers:registration.cancelSignup')}
            </DialogActionPrimary>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* Unassigned people warning dialog */}
      <Dialog open={unassignedWarningOpen} onOpenChange={setUnassignedWarningOpen}>
        <DialogContent title={t('volunteers:registration.unassignedWarningTitle')} supportingText={t('volunteers:registration.unassignedWarningMessage')} purpose="warning" icon="alert-triangle" onInteractOutside={e => e.preventDefault()}>
          <ul className="flex flex-col gap-snug-sm mb-comfy-md">
            {logic.contactsWithoutShifts.map(c => <li key={c.id} className="label-potent label-md">
                {[c.firstName, c.lastName].filter(Boolean).join(' ')}
              </li>)}
          </ul>
          <DialogActions>
            <DialogActionClose>
              {t('volunteers:registration.unassignedWarningCancel')}
            </DialogActionClose>
            <DialogActionPrimary onClick={handleConfirmUnassigned}>
              {t('volunteers:registration.unassignedWarningConfirm')}
            </DialogActionPrimary>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>;
};